import { useLayoutEffect, type RefObject } from 'react';

// CSS custom properties read by index.css and by the chat widgets to keep the
// floating button clear of each step's fixed chrome. Named `chat-widget`
// rather than `livechat` because both entry points (LiveChat bubble and
// WhatsApp button) position themselves with them.
export const CHAT_WIDGET_BOTTOM_OFFSET_PROPERTY = '--chat-widget-bottom-offset';
export const CHAT_WIDGET_TOP_OFFSET_PROPERTY = '--chat-widget-top-offset';

const PROPERTY_BY_EDGE = {
  bottom: CHAT_WIDGET_BOTTOM_OFFSET_PROPERTY,
  top: CHAT_WIDGET_TOP_OFFSET_PROPERTY,
} as const;

export type ChatWidgetOffsetEdge = keyof typeof PROPERTY_BY_EDGE;

/**
 * Publishes the rendered height of a step's fixed chrome as a CSS custom
 * property on the document, so the chat widget sits right beside it instead of
 * on top of it.
 *
 * `bottom` (default) measures the fixed bottom bar — the shared funnel CTA, or
 * the taller checkout bar, whichever the step renders. `top` measures the
 * header block, used by the top-* widget positions.
 *
 * The property is removed while the element has no height (e.g. hidden on
 * desktop) and on unmount, so the stylesheet default applies again.
 */
export function useChatWidgetOffset(barRef: RefObject<HTMLElement | null>, edge: ChatWidgetOffsetEdge = 'bottom') {
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const property = PROPERTY_BY_EDGE[edge];
    const root = document.documentElement;
    const publish = () => {
      const height = Math.round(bar.getBoundingClientRect().height);
      if (height > 0) {
        root.style.setProperty(property, `${height}px`);
      } else {
        root.style.removeProperty(property);
      }
    };
    const cleanup = () => root.style.removeProperty(property);
    publish();
    if (typeof ResizeObserver === 'undefined') return cleanup;
    const observer = new ResizeObserver(publish);
    observer.observe(bar);
    return () => {
      observer.disconnect();
      cleanup();
    };
  }, [barRef, edge]);
}
